/** Палитра аватарок — как в мессенджерах: цвет закреплён за человеком. */
const AVATAR_COLORS = [
  '#e17076', '#7bc862', '#e5ca77', '#65aadd',
  '#a695e7', '#ee7aae', '#6ec9cb', '#faa774',
]

export function avatarColor(id: string): string {
  let sum = 0
  for (let i = 0; i < id.length; i += 1) sum = (sum + id.charCodeAt(i)) % 1024
  return AVATAR_COLORS[sum % AVATAR_COLORS.length] ?? AVATAR_COLORS[0]!
}

/** Инициалы: «Анна Ким» → «АК», «Мама» → «М». */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? '?'
  const second = parts.length > 1 ? (parts[1]?.[0] ?? '') : ''
  return (first + second).toUpperCase()
}

const time = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' })
const weekday = new Intl.DateTimeFormat('ru-RU', { weekday: 'short' })
const dayMonth = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' })
const dayMonthYear = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

/** Разница в днях: сегодня 0, вчера 1. Считается по календарю, а не по часам. */
const daysAgo = (date: Date): number =>
  Math.round((startOfDay(new Date()) - startOfDay(date)) / 86_400_000)

export const messageTime = (iso: string): string => time.format(new Date(iso))

/** Время в списке чатов: сегодня — часы, на неделе — день недели, дальше — дата. */
export function chatListTime(iso: string): string {
  const date = new Date(iso)
  const days = daysAgo(date)
  if (days === 0) return time.format(date)
  if (days === 1) return 'вчера'
  if (days < 7) return weekday.format(date)
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit' }).format(date)
}

/** Разделитель дат в ленте сообщений. */
export function dateSeparator(iso: string): string {
  const date = new Date(iso)
  const days = daysAgo(date)
  if (days === 0) return 'Сегодня'
  if (days === 1) return 'Вчера'
  if (date.getFullYear() === new Date().getFullYear()) return dayMonth.format(date)
  return dayMonthYear.format(date)
}

/**
 * Когда уйдёт отложенное: «сегодня в 21:00», «завтра в 9:00»,
 * «3 октября в 9:00».
 *
 * Дату без слова «сегодня» человек всё равно мысленно переводит
 * в «это когда?» — лучше перевести за него.
 */
export function whenItGoes(iso: string): string {
  const date = new Date(iso)
  const days = -daysAgo(date)
  const at = time.format(date)
  if (days === 0) return `сегодня в ${at}`
  if (days === 1) return `завтра в ${at}`
  if (date.getFullYear() === new Date().getFullYear()) return `${dayMonth.format(date)} в ${at}`
  return `${dayMonthYear.format(date)} в ${at}`
}

export const sameDay = (a: string, b: string): boolean =>
  startOfDay(new Date(a)) === startOfDay(new Date(b))

/** «был в сети 5 минут назад» — для шапки переписки. */
export function lastSeenText(iso: string | null, online: boolean): string {
  if (online) return 'в сети'
  if (!iso) return 'давно не заходил'

  const date = new Date(iso)
  const minutes = Math.floor((Date.now() - date.getTime()) / 60_000)

  if (minutes < 1) return 'был только что'
  if (minutes < 60) return `был ${minutes} ${plural(minutes, 'минуту', 'минуты', 'минут')} назад`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `был ${hours} ${plural(hours, 'час', 'часа', 'часов')} назад`

  const days = daysAgo(date)
  if (days === 1) return 'был вчера'
  return `был ${dayMonth.format(date)}`
}

/** Русские окончания: 1 минуту, 2 минуты, 5 минут. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 14) return many
  const mod10 = n % 10
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return few
  return many
}
